import { useState } from 'react'

export type Lang = 'en' | 'fr' | 'ar'

export interface Translations {
  nav: {
    how: string
    map: string
    testimonials: string
    contact: string
    login: string
    cta: string
  }
  hero: {
    eyebrow: string
    titlePre: string
    role: { expediteur: string; livreur: string; admin: string }
    titleMid: string
    titleAnd: string
    titlePost: string
    sub: { expediteur: string; livreur: string; admin: string }
    cta: { expediteur: string; livreur: string; admin: string }
    howBtn: string
    trust: string
  }
  stats: {
    deliveries: { label: string; trend: string }
    couriers:   { label: string; trend: string }
    businesses: { label: string; trend: string }
    ops:        { label: string; trend: string }
  }
  how: {
    kicker: string
    title: string
    sub: string
    step1: { tag: string; title: string; desc: string }
    step2: { tag: string; title: string; desc: string }
    step3: { tag: string; title: string; desc: string }
  }
  map: {
    kicker: string
    title: string
    sub: string
    bullets: string[]
  }
  t: {
    kicker: string
    title: string
    cards: { quote: string; name: string; role_: string; tag: string }[]
  }
  contact: {
    kicker: string
    title: string
    sub: string
    email: string
    phone: string
    hq: string
    form: {
      name: string
      company: string
      email: string
      phone: string
      role_: string
      volume: string
      volumes: string[]
      submit: string
      success: string
    }
  }
  footer: {
    tagline: string
    cols: { title: string; links: string[] }[]
    status: string
    copy: string
  }
  modal: {
    title: string
    sub: string
    emailLabel: string
    passLabel: string
    submit: string
    loading: string
    forgot: string
    register: string
  }
  preview: {
    thisMonth: string
    inProgress: string
    revenue: string
    delivered: string
    inTransit: string
    pickup: string
    today: string
    deliveries: string
    online: string
    sla: string
    delays: string
    newOrder: string
    accept: string
    decline: string
  }
}

// ── English ────────────────────────────────────────────────
export const en: Translations = {
  nav: {
    how: 'How it works',
    map: 'Live tracking',
    testimonials: 'Customers',
    contact: 'Contact',
    login: 'Sign in',
    cta: 'Get started',
  },
  hero: {
    eyebrow: 'Live across Tunis · Sfax · Sousse · Nabeul',
    titlePre: 'Built for',
    role: { expediteur: 'shippers', livreur: 'couriers', admin: 'operations' },
    titleMid: ',',
    titleAnd: 'and',
    titlePost: '.',
    sub: {
      expediteur: 'Create packages in 20 seconds, track deliveries in real time and collect your money automatically.',
      livreur: 'Accept orders, navigate, scan and get paid every Friday — no paperwork.',
      admin: 'Manage your entire fleet from one dashboard. Exceptions surface automatically, payments reconcile themselves.',
    },
    cta: {
      expediteur: 'Start shipping',
      livreur: 'Join the fleet',
      admin: 'Admin access',
    },
    howBtn: 'See how it works',
    trust: 'businesses trust TraiqGo across Tunisia',
  },
  stats: {
    deliveries: { label: 'Deliveries completed',   trend: '+12.4% this month' },
    couriers:   { label: 'Active couriers',         trend: '438 online right now' },
    businesses: { label: 'Businesses shipping',     trend: '+86 joined this week' },
    ops:        { label: 'Operations control',      trend: 'Avg response 12 min' },
  },
  how: {
    kicker: 'How it works',
    title: 'Three actors. One workflow. Zero spreadsheets.',
    sub: 'From the moment a package is created to the electronic signature, TraiqGo keeps everyone in sync — in real time.',
    step1: {
      tag: 'Shipper',
      title: 'Create the package in 20 seconds.',
      desc: 'Address, size, cash on delivery. TraiqGo generates the barcode and receipt automatically.',
    },
    step2: {
      tag: 'Courier',
      title: 'The nearest courier accepts in seconds.',
      desc: 'Our matching algorithm assigns the order to the most available courier with the best capacity.',
    },
    step3: {
      tag: 'Admin',
      title: 'Operations see everything, live.',
      desc: 'One dashboard for the entire fleet — exceptions surface automatically, payments reconcile themselves.',
    },
  },
  map: {
    kicker: 'Live tracking',
    title: 'Every package, on the map, in real time.',
    sub: 'Shippers track without calling. Couriers navigate. Admins detect delays before they become problems.',
    bullets: [
      'GPS every 4 seconds, battery-optimised',
      'Geofenced pickup & delivery confirmations',
      'ETA recalculated in real time with traffic',
      'Shareable tracking link — no app needed for end customers',
    ],
  },
  t: {
    kicker: 'Customer reviews',
    title: 'From the startup founder to the courier on his scooter.',
    cards: [
      { quote: '"We cut dispatch time by 70%. The admin dashboard alone is worth the investment."', name: 'Salma Mejri', role_: 'Operations, Karaz Express', tag: 'Admin' },
      { quote: '"I went from 12 deliveries a day to 22. The routing really understands Tunisian traffic."', name: 'Youssef Karoui', role_: 'Courier, Tunis Centre', tag: 'Courier' },
      { quote: '"Our customers stopped calling to ask where their package is. Support tickets dropped 40%."', name: 'Aymen Ben Ali', role_: 'Founder, Olive & Co.', tag: 'Shipper' },
    ],
  },
  contact: {
    kicker: 'Get started',
    title: 'Ship your first package with TraiqGo this week.',
    sub: "Tell us your volume and we'll onboard you in 48h — with a dedicated success manager for your first month.",
    email: 'Email',
    phone: 'Phone',
    hq: 'HQ',
    form: {
      name: 'Full name',
      company: 'Company',
      email: 'Email',
      phone: 'Phone',
      role_: "I'm signing up as",
      volume: 'Expected volume',
      volumes: [
        'Less than 50 deliveries / month',
        '50 – 500 deliveries / month',
        '500 – 5,000 deliveries / month',
        'More than 5,000 deliveries / month',
      ],
      submit: 'Request access',
      success: "Thank you — we'll be in touch within 24 hours.",
    },
  },
  footer: {
    tagline: 'The delivery CRM connecting shippers, couriers and admins across Tunisia.',
    cols: [
      { title: 'Product',   links: ['For shippers', 'For couriers', 'For admins', 'API', 'Pricing'] },
      { title: 'Company',   links: ['About', 'Careers', 'Press', 'Contact'] },
      { title: 'Resources', links: ['Help', 'Status', 'Docs', 'Changelog'] },
      { title: 'Legal',     links: ['Terms', 'Privacy', 'Cookies'] },
    ],
    status: 'All systems operational',
    copy: '© 2026 TraiqGo · Made in Tunis',
  },
  modal: {
    title: 'Welcome back',
    sub: 'Sign in to your TraiqGo account',
    emailLabel: 'Email',
    passLabel: 'Password',
    submit: 'Sign in →',
    loading: 'Signing in…',
    forgot: 'Forgot password?',
    register: 'Create an account',
  },
  preview: {
    thisMonth: 'This month',
    inProgress: 'In progress',
    revenue: 'Revenue',
    delivered: 'Delivered',
    inTransit: 'In transit',
    pickup: 'Pickup',
    today: 'Today',
    deliveries: 'Deliveries',
    online: 'Online',
    sla: 'SLA',
    delays: 'Delays',
    newOrder: 'New order assigned',
    accept: 'Accept →',
    decline: 'Decline',
  },
}

// ── French ─────────────────────────────────────────────────
export const fr: Translations = {
  nav: {
    how: 'Comment ça marche',
    map: 'Tracking live',
    testimonials: 'Avis clients',
    contact: 'Contact',
    login: 'Connexion',
    cta: 'Démarrer',
  },
  hero: {
    eyebrow: 'Opérationnel à Tunis · Sfax · Sousse · Nabeul',
    titlePre: 'Conçu pour les',
    role: { expediteur: 'expéditeurs', livreur: 'livreurs', admin: 'opérations' },
    titleMid: ',',
    titleAnd: 'et les',
    titlePost: '.',
    sub: {
      expediteur: 'Créez vos colis en 20 secondes, suivez vos livraisons en temps réel et récupérez votre argent automatiquement.',
      livreur: 'Acceptez les commandes, naviguez, scannez et soyez payé chaque vendredi — sans paperasse.',
      admin: 'Pilotez toute la flotte depuis un seul dashboard. Exceptions en surface, paiements réconciliés automatiquement.',
    },
    cta: {
      expediteur: 'Commencer à expédier',
      livreur: 'Rejoindre la flotte',
      admin: 'Accès admin',
    },
    howBtn: 'Voir comment ça marche',
    trust: 'entreprises font confiance à TraiqGo à travers la Tunisie',
  },
  stats: {
    deliveries: { label: 'Livraisons effectuées',  trend: '+12,4% ce mois' },
    couriers:   { label: 'Livreurs actifs',         trend: '438 en ligne maintenant' },
    businesses: { label: 'Entreprises clientes',    trend: '+86 cette semaine' },
    ops:        { label: 'Contrôle opérations',     trend: 'Réponse moy. 12 min' },
  },
  how: {
    kicker: 'Comment ça marche',
    title: 'Trois acteurs. Un workflow. Zéro tableur.',
    sub: 'De la création du colis à la signature électronique, TraiqGo synchronise tout en temps réel.',
    step1: {
      tag: 'Expéditeur',
      title: 'Créez le colis en 20 secondes.',
      desc: 'Adresse, taille, paiement à la livraison. TraiqGo génère le code-barres et le reçu automatiquement.',
    },
    step2: {
      tag: 'Livreur',
      title: 'Le livreur le plus proche accepte en quelques secondes.',
      desc: 'Notre algorithme de matching envoie la commande au livreur disponible avec la meilleure capacité.',
    },
    step3: {
      tag: 'Admin',
      title: 'Les opérations voient tout, en direct.',
      desc: 'Un seul dashboard pour toute la flotte — les exceptions remontent automatiquement, les paiements se réconcilient seuls.',
    },
  },
  map: {
    kicker: 'Tracking live',
    title: 'Chaque colis, sur la carte, en temps réel.',
    sub: "Les expéditeurs suivent sans appeler. Les livreurs naviguent. Les admins détectent les retards avant qu'ils ne deviennent des problèmes.",
    bullets: [
      'GPS toutes les 4 secondes, optimisé pour la batterie',
      'Confirmation de pickup & livraison géofencée',
      'ETA recalculé en temps réel selon le trafic',
      'Lien de tracking partageable — sans application pour le client final',
    ],
  },
  t: {
    kicker: 'Avis clients',
    title: 'Du fondateur de startup au livreur sur son scooter.',
    cards: [
      { quote: "« On a réduit notre temps de dispatch de 70%. Le dashboard admin à lui seul vaut l'investissement. »", name: 'Salma Mejri', role_: 'Opérations, Karaz Express', tag: 'Admin' },
      { quote: '« Je suis passé de 12 livraisons par jour à 22. Le routing comprend vraiment le trafic tunisien. »', name: 'Youssef Karoui', role_: 'Livreur, Tunis Centre', tag: 'Livreur' },
      { quote: "« Nos clients ont arrêté d'appeler pour savoir où est leur colis. Les tickets support ont chuté de 40%. »", name: 'Aymen Ben Ali', role_: 'Fondateur, Olive & Co.', tag: 'Expéditeur' },
    ],
  },
  contact: {
    kicker: 'Démarrer',
    title: 'Envoyez votre premier colis avec TraiqGo cette semaine.',
    sub: 'Dites-nous votre volume et nous vous onboardons en 48 h — avec un success manager dédié le premier mois.',
    email: 'Email',
    phone: 'Téléphone',
    hq: 'Siège',
    form: {
      name: 'Nom complet',
      company: 'Entreprise',
      email: 'Email',
      phone: 'Téléphone',
      role_: "Je m'inscris en tant que",
      volume: 'Volume estimé',
      volumes: [
        'Moins de 50 livraisons / mois',
        '50 – 500 livraisons / mois',
        '500 – 5 000 livraisons / mois',
        'Plus de 5 000 livraisons / mois',
      ],
      submit: "Demander l'accès",
      success: 'Merci — nous vous répondons sous 24 h.',
    },
  },
  footer: {
    tagline: 'Le CRM de livraison qui connecte expéditeurs, livreurs et admins à travers la Tunisie.',
    cols: [
      { title: 'Produit',    links: ['Pour expéditeurs', 'Pour livreurs', 'Pour admins', 'API', 'Tarifs'] },
      { title: 'Entreprise', links: ['À propos', 'Carrières', 'Presse', 'Contact'] },
      { title: 'Ressources', links: ['Aide', 'Statut', 'Documentation', 'Changelog'] },
      { title: 'Légal',      links: ['CGU', 'Confidentialité', 'Cookies'] },
    ],
    status: 'Tous les systèmes opérationnels',
    copy: '© 2026 TraiqGo · Fait à Tunis',
  },
  modal: {
    title: 'Bon retour',
    sub: 'Connectez-vous à votre espace TraiqGo',
    emailLabel: 'Email',
    passLabel: 'Mot de passe',
    submit: 'Se connecter →',
    loading: 'Connexion…',
    forgot: 'Mot de passe oublié ?',
    register: 'Créer un compte',
  },
  preview: {
    thisMonth: 'Ce mois',
    inProgress: 'En cours',
    revenue: 'CA',
    delivered: 'Livré',
    inTransit: 'En route',
    pickup: 'Pickup',
    today: "Aujourd'hui",
    deliveries: 'Livraisons',
    online: 'En ligne',
    sla: 'SLA',
    delays: 'Retards',
    newOrder: 'Nouvelle commande assignée',
    accept: 'Accepter →',
    decline: 'Refuser',
  },
}

// ── Arabic (RTL) ───────────────────────────────────────────
export const ar: Translations = {
  nav: {
    how: 'كيف يعمل',
    map: 'التتبع المباشر',
    testimonials: 'آراء العملاء',
    contact: 'تواصل معنا',
    login: 'تسجيل الدخول',
    cta: 'ابدأ الآن',
  },
  hero: {
    eyebrow: 'نشط في تونس · صفاقس · سوسة · نابل',
    titlePre: 'مصمم لـ',
    role: { expediteur: 'المرسِلين', livreur: 'المندوبين', admin: 'العمليات' },
    titleMid: '،',
    titleAnd: 'و',
    titlePost: '.',
    sub: {
      expediteur: 'أنشئ طرودك في 20 ثانية، تتبّع شحناتك في الوقت الفعلي واستلم أموالك تلقائياً.',
      livreur: 'اقبل الطلبات، تنقّل، امسح وكن مدفوعاً كل جمعة — بدون أوراق.',
      admin: 'أدر أسطولك بأكمله من لوحة تحكم واحدة. الاستثناءات تظهر تلقائياً، والمدفوعات تُسوَّى من تلقاء نفسها.',
    },
    cta: {
      expediteur: 'ابدأ الشحن',
      livreur: 'انضم للأسطول',
      admin: 'وصول المشرف',
    },
    howBtn: 'شاهد كيف يعمل',
    trust: 'شركة تثق في TraiqGo عبر تونس',
  },
  stats: {
    deliveries: { label: 'عمليات التوصيل المنجزة', trend: '+12.4% هذا الشهر' },
    couriers:   { label: 'المندوبون النشطون',       trend: '438 متصل الآن' },
    businesses: { label: 'الشركات المشتركة',        trend: '+86 هذا الأسبوع' },
    ops:        { label: 'مراقبة العمليات',          trend: 'متوسط الرد 12 دقيقة' },
  },
  how: {
    kicker: 'كيف يعمل',
    title: 'ثلاثة أطراف. سير عمل واحد. صفر جداول.',
    sub: 'من إنشاء الطرد حتى التوقيع الإلكتروني، يزامن TraiqGo كل شيء في الوقت الفعلي.',
    step1: {
      tag: 'المرسِل',
      title: 'أنشئ الطرد في 20 ثانية.',
      desc: 'العنوان، الحجم، الدفع عند الاستلام. يولّد TraiqGo الباركود والإيصال تلقائياً.',
    },
    step2: {
      tag: 'المندوب',
      title: 'أقرب مندوب يقبل في ثوانٍ.',
      desc: 'تُرسل خوارزمية المطابقة الطلب للمندوب المتاح بأفضل طاقة استيعابية.',
    },
    step3: {
      tag: 'المشرف',
      title: 'العمليات ترى كل شيء، مباشرةً.',
      desc: 'لوحة تحكم واحدة للأسطول بأكمله — الاستثناءات تظهر تلقائياً، والمدفوعات تُسوَّى وحدها.',
    },
  },
  map: {
    kicker: 'التتبع المباشر',
    title: 'كل طرد، على الخريطة، في الوقت الفعلي.',
    sub: 'المرسلون يتتبعون دون اتصال. المندوبون يتنقلون. المشرفون يكتشفون التأخيرات قبل أن تصبح مشاكل.',
    bullets: [
      'GPS كل 4 ثوانٍ، محسَّن لاستهلاك البطارية',
      'تأكيدات الاستلام والتسليم بالسياج الجغرافي',
      'إعادة حساب وقت الوصول في الوقت الفعلي',
      'رابط تتبع قابل للمشاركة — لا تطبيق للعميل النهائي',
    ],
  },
  t: {
    kicker: 'آراء العملاء',
    title: 'من مؤسس الشركة إلى المندوب على دراجته.',
    cards: [
      { quote: '"قلّصنا وقت التوزيع بنسبة 70%. لوحة تحكم المشرف وحدها تستحق الاستثمار."', name: 'سلمى مجري', role_: 'مسؤولة العمليات، Karaz Express', tag: 'مشرف' },
      { quote: '"انتقلت من 12 توصيلة يومياً إلى 22. نظام التوجيه يفهم حقاً حركة المرور التونسية."', name: 'يوسف القروي', role_: 'مندوب، تونس المدينة', tag: 'مندوب' },
      { quote: '"عملاؤنا توقفوا عن الاتصال لسؤال أين طردهم. انخفضت تذاكر الدعم بنسبة 40%."', name: 'أيمن بن علي', role_: 'مؤسس، Olive & Co.', tag: 'مرسِل' },
    ],
  },
  contact: {
    kicker: 'ابدأ الآن',
    title: 'أرسل أول طرد مع TraiqGo هذا الأسبوع.',
    sub: 'أخبرنا بحجمك وسنقوم بإعدادك خلال 48 ساعة — مع مدير نجاح مخصص للشهر الأول.',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    hq: 'المقر',
    form: {
      name: 'الاسم الكامل',
      company: 'الشركة',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      role_: 'أسجّل بصفتي',
      volume: 'الحجم المتوقع',
      volumes: [
        'أقل من 50 توصيلة / شهر',
        '50 – 500 توصيلة / شهر',
        '500 – 5,000 توصيلة / شهر',
        'أكثر من 5,000 توصيلة / شهر',
      ],
      submit: 'طلب الوصول',
      success: 'شكراً — سنتواصل معك خلال 24 ساعة.',
    },
  },
  footer: {
    tagline: 'نظام CRM للتوصيل يربط المرسلين والمندوبين والمشرفين عبر تونس.',
    cols: [
      { title: 'المنتج',   links: ['للمرسلين', 'للمندوبين', 'للمشرفين', 'API', 'الأسعار'] },
      { title: 'الشركة',   links: ['من نحن', 'وظائف', 'صحافة', 'تواصل معنا'] },
      { title: 'الموارد',  links: ['المساعدة', 'الحالة', 'التوثيق', 'سجل التغييرات'] },
      { title: 'قانوني',   links: ['الشروط', 'الخصوصية', 'الكوكيز'] },
    ],
    status: 'جميع الأنظمة تعمل',
    copy: '© 2026 TraiqGo · صُنع في تونس',
  },
  modal: {
    title: 'مرحباً بعودتك',
    sub: 'سجّل الدخول إلى حسابك في TraiqGo',
    emailLabel: 'البريد الإلكتروني',
    passLabel: 'كلمة المرور',
    submit: 'تسجيل الدخول ←',
    loading: 'جارٍ التسجيل…',
    forgot: 'نسيت كلمة المرور؟',
    register: 'إنشاء حساب',
  },
  preview: {
    thisMonth: 'هذا الشهر',
    inProgress: 'قيد التنفيذ',
    revenue: 'الإيراد',
    delivered: 'مُسلَّم',
    inTransit: 'في الطريق',
    pickup: 'استلام',
    today: 'اليوم',
    deliveries: 'التوصيلات',
    online: 'متصل',
    sla: 'SLA',
    delays: 'التأخيرات',
    newOrder: 'طلب جديد مُعيَّن',
    accept: '← قبول',
    decline: 'رفض',
  },
}

// ── Hook ───────────────────────────────────────────────────
const TRANSLATIONS: Record<Lang, Translations> = { en, fr, ar }

export function useI18n() {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem('traiqgo-lang') as Lang | null
    if (saved && ['en', 'fr', 'ar'].includes(saved)) return saved
    const browser = navigator.language.toLowerCase()
    if (browser.startsWith('ar')) return 'ar'
    if (browser.startsWith('fr')) return 'fr'
    return 'fr'
  })

  const changeLang = (l: Lang) => {
    setLang(l)
    localStorage.setItem('traiqgo-lang', l)
    document.documentElement.dir = l === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = l
  }

  // Apply dir on mount
  if (typeof document !== 'undefined') {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = lang
  }

  return { lang, t: TRANSLATIONS[lang], changeLang }
}
